<template>
  <Modal
    v-model="modalVisible"
    :mask-closable="false"
    class-name="choose-goods-modal"
    footer-hide
    width="868"
    @on-visible-change="onVisibleChange"
  >
    <div class="goods-dialog">
      <!-- 头部：Tab 栏 + 搜索框 同一行 -->
      <div class="header-top">
        <div class="tab-bar">
          <div
            v-for="(tab, index) in tabList" :key="index"
            class="tab-item" :class="{ active: tabIndex === index }"
            @click="onTabChange(index)"
          >{{ tab.name }}</div>
        </div>

        <div class="search-row">
          <input
            v-model="keyword"
            placeholder="搜索关键字进行搜索"
            @keyup.enter="onSearch"
            @compositionend="onSearch"
          />
          <div class="search-btn" @click="onSearch">搜索</div>
        </div>
      </div>

      <!-- 选品广场：分类标签 -->
      <div v-if="tabIndex === 1 && cateList.length" class="cate-bar">
        <div class="cate-item" :class="{ active: productCategoryId === item.product_category_id }" v-for="(item, index) in cateList" :key="index"  @click="clickCate(item.product_category_id)">
          <span>{{ item.category_name_cn }}</span>
          <img v-if="productCategoryId === item.product_category_id" src="@/assets/images/cate-active.png" alt="" class="mall-icon" />
        </div>
      </div>

      <!-- 排序栏 -->
      <div class="sort-bar">
        <span
          v-for="(item, index) in sortList" :key="index"
          class="sort-item" :class="{ active: currentSort === index }"
          @click="onSortClick(index)"
        >
          {{ item.name }}
          <i v-if="item.is_sort" class="arrow-icon" :class="{ asc: item.sort === 'asc', desc: item.sort === 'desc' }"></i>
        </span>
      </div>

      <!-- 商品列表：两列网格 + 横向卡片（左图右文，结构与小程序 goodsListCardSelect 一致） -->
      <div class="goods-list" ref="listContainer" @scroll="onScroll">
        <div class="grid-wrap">
          <div
            v-for="(item, index) in list" :key="'g-' + index"
            class="goods-card" :class="{ selected: isSelected(item) }"
            @click="onSelect(item)"
          >
            <!-- 左：商品图 -->
            <div class="card-img">
              <img :src="item.image | default_img" alt="" />
            </div>
            <!-- 右：信息 -->
            <div class="card-info">
              <div class="card-name">{{ item.name }}</div>
              <div class="card-price-row">
                <span class="price">¥{{ item.sale_price }}</span>
                <span class="sales">已售{{ item.sales > 0 ? item.sales : 0 }}+</span>
              </div>
              <div class="card-commission" v-if="item.is_join_commission === 1">
                <span>佣金率{{ item.product_commission }}%</span>
                <span>赚¥{{ item.commission_price }}</span>
              </div>
              <div class="card-shop" v-if="item.shop">
                <img :src="item.shop.logo | default_img" class="shop-avatar" />
                <span class="shop-name">{{ item.shop.shop_name }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="empty-tip" v-if="!loading && !list.length">暂无商品</div>
        <div class="load-more" v-if="loading"><Spin /> 加载中...</div>
      </div>

      <!-- 底部按钮 -->
      <div class="dialog-footer">
        <Button class="footer-btn cancel-btn" @click="onCancel">取消</Button>
        <Button class="footer-btn confirm-btn" type="error" :disabled="!selectedItem" @click="onConfirm">确认添加</Button>
      </div>
    </div>
  </Modal>
</template>

<script>
import { getMallProductList, getMallCategoryList } from '@/api/index'

export default {
  name: 'ChooseGoodsDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modalVisible: false,

      // Tab
      tabIndex: 0,
      tabList: [
        { name: '我的店铺' },
        { name: '选品广场' },
      ],

      // 搜索 & 分类
      keyword: '',
      cateList: [],
      productCategoryId: 0,

      // 列表
      list: [],
      page: 1,
      limit: 20,
      total: 0,
      noMore: false,
      loading: false,

      // 排序
      currentSort: 0,
      sortList: [
        { name: '综合', type: '', sort: '', is_sort: false },
        { name: '销量', type: 'sales', sort: '', is_sort: true },
        { name: '商品价格', type: 'sale_price', sort: '', is_sort: true },
        { name: '佣金', type: 'commission_price', sort: '', is_sort: true },
      ],

      // 选中
      selectedItem: null,
    }
  },
  watch: {
    visible(val) {
      this.modalVisible = val
      if (val) {
        this.resetAndLoad()
      }
    },
  },
  computed: {
    userInfo() {
      return this.$store.state.userInfo || {}
    },
  },
  methods: {
    onVisibleChange(val) {
      this.$emit('update:visible', val)
      if (!val) {
        this.$emit('cancel')
      }
      console.log('this.userInfo', this.userInfo)
    },

    resetAndLoad() {
      this.tabIndex = this.userInfo.is_shop_keeper === 1 ? 0 : 1
      this.keyword = ''
      this.productCategoryId = 0
      this.list = []
      this.page = 1
      this.noMore = false
      this.selectedItem = null
      this.sortList.forEach((s) => { s.sort = '' })
      this.currentSort = 0
      this.getCateList()
      this.loadData()
    },

    onTabChange(index) {
      if (this.tabIndex === index) return
      this.tabIndex = index
      this.keyword = ''
      this.productCategoryId = 0
      this.list = []
      this.page = 1
      this.noMore = false
      this.selectedItem = null
      if (this.tabIndex === 1) {
        this.getCateList()
      }
      this.loadData()
    },

    async getCateList() {
      try {
        const { data } = await getMallCategoryList()
        this.cateList = [{ product_category_id: 0, category_name_cn: '全部' }, ...(data || [])]
      } catch (e) {
        console.error('获取分类失败', e)
      }
    },

    clickCate(id) {
      this.productCategoryId = id
      this.list = []
      this.page = 1
      this.noMore = false
      this.loadData()
    },

    onSearch() {
      this.list = []
      this.page = 1
      this.noMore = false
      this.loadData()
    },

    onSortClick(index) {
      this.sortList.forEach((item, i) => {
        if (i === index && item.is_sort) {
          item.sort = item.sort === 'desc' ? 'asc' : 'desc'
        } else if (item.is_sort) {
          item.sort = ''
        }
      })
      this.currentSort = index
      this.list = []
      this.page = 1
      this.noMore = false
      this.loadData()
    },

    async loadData() {
      if (this.noMore || this.loading) return
      this.loading = true
      try {
        const param = {
          page: this.page,
          limit: this.limit,
          is_commission: true,
          code: this.tabIndex === 0 ? this.userInfo.code : undefined,
          keyword: this.keyword || undefined,
          category_id: this.productCategoryId || undefined,
          sort_field: this.sortList[this.currentSort].type || undefined,
          sort_order: this.sortList[this.currentSort].sort || undefined,
          source: 'zone',
        }

        const { data } = await getMallProductList(param)
        const newList = data.list || []
        this.total = data.total || 0

        if (this.page === 1) {
          this.list = newList
        } else {
          this.list = [...this.list, ...newList]
        }

        this.noMore = newList.length < this.limit
        this.page++
      } catch (e) {
        console.error('加载商品失败', e)
      } finally {
        this.loading = false
      }
    },

    onScroll(e) {
      const el = e.target
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
        this.loadData()
      }
    },

    isSelected(item) {
      return this.selectedItem && this.selectedItem.uuid === item.uuid
    },

    onSelect(item) {
      this.selectedItem = item
    },

    onCancel() {
      this.modalVisible = false
      this.$emit('update:visible', false)
      this.$emit('cancel')
    },

    onConfirm() {
      if (!this.selectedItem) {
        this.$Message.warning('请选择商品')
        return
      }
      this.$emit('confirm', this.selectedItem)
      this.modalVisible = false
      this.$emit('update:visible', false)
    },
  },
}
</script>

<style lang="scss" scoped>
.goods-dialog {
  display: flex;
  flex-direction: column;
  height: 700px;
  text-align: left;
  line-height: 1.5;
}

/* ====== 顶部：Tab + 搜索框同行 ====== */
.header-top {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e3e3e3;
  padding: 20px 30px;
  padding-bottom: 0 !important;

  .tab-bar {
    display: flex;
    align-items: center;
    gap: 48px;

    .tab-item {
      font-size: 20px;
      color: #666666;
      padding-bottom: 16px;
      cursor: pointer;
      position: relative;

      &.active {
        color: #FF5967;
        font-weight: bold;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 3px;
          background: #ff5967;
        }
      }
    }
  }

  .search-row {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f3f4f6;
    border-radius: 24px;
    padding: 8px 16px;
    width: 427px;
    height: 41px;
    box-sizing: border-box;

    input {
        flex: 1;
        border: none;
        outline: none;
        background: transparent;
        font-size: 14px;
        min-width: 0;
    }

    .search-btn {
        width: 92px;
        height: 33px;
        background: #FF5967;
        border-radius: 20px 20px 20px 20px;
        font-size: 14px;
        color: #fff;
        font-weight: bold;
        cursor: pointer;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
    }
  }
}

/* ====== 分类标签（仅选品广场） ====== */
.cate-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 8px 30px 0px;
  overflow-x: auto;
  white-space: nowrap;

  &::-webkit-scrollbar {
    display: none;
  }

  .cate-item {
    font-size: 14px;
    color: #333333;
    cursor: pointer;
    padding: 3px 0;
    flex-shrink: 0;
    position: relative;

    &.active {
      font-weight: bold;
      font-size: 16px;
    }
  }
  .mall-icon {
    width: 22px;
    height: 11px;
    position: absolute;
    bottom: 0;
    right: -6px;
  }
}

/* ====== 排序栏 ====== */
.sort-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 30px 0;

  .sort-item {
    font-size: 12px;
    color: #333333;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 3px;
    border-radius: 6px 6px 6px 6px;
    border: 1px solid #E3E3E3;
    padding: 5px 12px;

    &.active {
      font-weight: bold;
      color: #FF5967;
      border: 1px solid #FF5967;
    }

    .arrow-icon {
      display: inline-block;
      width: 0;
      height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;

      &.asc {
        border-bottom: 5px solid #000;
      }

      &.desc {
        border-top: 5px solid #000;
      }
    }
  }
}

/* ====== 商品列表：两列网格 ====== */
.goods-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px 30px;

  .grid-wrap {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px 14px;
    align-content: start;
  }

  /* 横向卡片：左图右文，结构对齐小程序 goodsListCardSelect */
  .goods-card {
    display: flex;
    align-items: stretch;
    background: #fff;
    border: 1px solid transparent;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: border-color 0.15s;

    &:hover {
      border-color: #f0a9af;
    }

    /* 选中态：整卡红色边框 */
    &.selected {
      border-color: #FF5967;
    }

    /* 左：商品图 固定尺寸 3:4 */
    .card-img {
      width: 120px;
      height: 157px;
      flex-shrink: 0;
      background: #e3e3e3;
      border-radius: 7px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        border-radius: 7px;
      }
    }

    /* 右：信息 */
    .card-info {
      flex: 1;
      min-width: 0;
      padding: 8px 10px;
      display: flex;
      flex-direction: column;

      .card-name {
        font-size: 14px;
        color: #333333;
        font-weight: 500;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        word-break: break-word;
      }

      .card-price-row {
        display: flex;
        align-items: baseline;
        gap: 10px;
        margin-top: 8px;

        .price {
          font-size: 14px;
          font-weight: bold;
          color: #FA3D5B;
        }

        .sales {
          font-size: 12px;
          color: #999999;
        }
      }

      .card-commission {
        margin-top: 10px;
        padding: 2px 6px;
        width: 100%;
        display: inline-flex;
        align-items: center;
        align-self: flex-start;
        gap: 10px;
        font-size: 12px;
        color: #FF0F23;
        background: #F8F9FB;
        border-radius: 2px;
      }

      .card-shop {
        margin-top: auto;
        padding-top: 8px;
        display: flex;
        align-items: center;
        gap: 6px;

        .shop-avatar {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          object-fit: cover;
          background: #eee;
          flex-shrink: 0;
        }

        .shop-name {
          font-size: 12px;
          color: #999;
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }

  .empty-tip {
    text-align: center;
    color: #999;
    padding: 80px 0;
    font-size: 14px;
  }

  .load-more {
    text-align: center;
    padding: 20px 0;
    color: #999;
    font-size: 13px;
  }
}

/* ====== 底部按钮 ====== */
.dialog-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 14px 24px;
  border-top: 1px solid #f0f0f0;

  .footer-btn {
    min-width: 88px;
    height: 36px;
    border-radius: 18px;
    font-size: 14px;
  }

  .cancel-btn {
    border: 1px solid #999;
    color: #999;
    background: #fff;

    &:hover {
      border-color: #666;
      color: #666;
    }
  }

  .confirm-btn {
    background: #FF0F23;
    border-color: #FF0F23;

    &[disabled] {
      background: #ffccc8;
      border-color: #ffccc8;
      color: #fff;
    }
  }
}
</style>

<!-- 重置 Modal body 内边距 -->
<style lang="scss">
.choose-goods-modal {
  .ivu-modal-body {
    padding: 0;
  }
}
</style>
